package br.com.htms.inspeccaocampo.platform

data class GpsFix(
    val latitude: Double,
    val longitude: Double,
    val textoLocal: String
)

data class CapturedPhoto(
    val path: String,
    val latitude: Double? = null,
    val longitude: Double? = null
)

/** Arquivo escolhido pelo usuário (PDF/imagem) para anexar a um cadastro. */
class PickedDocument(val name: String, val bytes: ByteArray)

expect object PlatformServices {
    val hasCamera: Boolean
    val hasGps: Boolean
    fun openUrl(url: String)
    suspend fun captureGps(): GpsFix?
    /** Abre a câmera; retorna caminho do arquivo JPEG/PNG salvo. */
    suspend fun capturePhoto(): CapturedPhoto?
    /** Seleciona imagem da galeria (banner). */
    suspend fun pickImage(): CapturedPhoto?
    /** Seleciona PDF/JPEG/PNG do aparelho; null se cancelado ou indisponível. */
    suspend fun pickDocument(): PickedDocument?
    /** Seleciona planilha (.xlsx ou .csv); null se cancelado ou indisponível. */
    suspend fun pickPlanilha(): PickedDocument?
    /** Seleciona arquivo .json (backup); null se cancelado ou indisponível. */
    suspend fun pickJson(): PickedDocument?
    /** Descompacta DEFLATE "raw" (entradas de arquivos .xlsx); null se indisponível. */
    suspend fun inflateRaw(data: ByteArray): ByteArray?
    /**
     * Converte traços da assinatura em PNG Base64 (sem prefixo data:).
     * strokes: lista de polilinhas; cada ponto é (x, y) em pixels.
     */
    fun encodeSignaturePngBase64(
        strokes: List<List<Pair<Float, Float>>>,
        width: Int,
        height: Int
    ): String?

    fun decodeBase64(base64: String): ByteArray?
    fun encodeBase64(bytes: ByteArray): String
    /**
     * Lê bytes da imagem. Em Web, se [path] for URL http(s) ou `r2:...`, busca no Worker R2
     * (com cache local). Em Android/iOS, caminho de arquivo local.
     */
    suspend fun loadImageBytes(path: String): ByteArray?
    /**
     * Assets empacotados do app (ex.: logo/banner do PDF na web).
     * Retorna null se não existir nesta plataforma.
     */
    suspend fun loadBundledAsset(fileName: String): ByteArray?
    /**
     * Após carimbo: na Web envia ao R2 e devolve URL (fonte durável ~60 dias);
     * em nativo devolve o mesmo [localPath].
     */
    suspend fun publishPhoto(localPath: String): String
    suspend fun saveBannerImage(bytes: ByteArray): String?
    /**
     * Web: embute JPEG no JSON da inspeção (PDF não depende só do IDB por path).
     * Android/iOS: false (arquivo local basta).
     */
    fun shouldEmbedPhotoBytesInForm(): Boolean

    /** false se o navegador/dispositivo reportar ausência de rede. */
    fun isNetworkAvailable(): Boolean

    /**
     * Web: avisa ao fechar/atualizar a aba se [active] (upload em andamento).
     * Android/iOS: no-op.
     */
    fun setUnloadGuard(active: Boolean)

    /**
     * Web: true se a aba fechou no meio de um envio (flag localStorage).
     * Consome a flag. Android/iOS: sempre false.
     */
    fun consumeUploadInflightFlag(): Boolean

    /**
     * Web: chamado em pagehide/aba em segundo plano para gravar rascunho.
     * Android/iOS: no-op.
     */
    fun setBackgroundFlush(onFlush: (() -> Unit)?)

    /**
     * Web: chamado quando a aba volta ao foco (visibility visible).
     * Útil para retomar reenvio após o Chrome mobile suspender a página.
     * Android/iOS: no-op.
     */
    fun setForegroundResume(onResume: (() -> Unit)?)

    /**
     * Web: marca que a câmera/galeria vai abrir (Safari costuma recarregar a página).
     * Android/iOS: no-op.
     */
    fun notifyPhotoPickerOpening()

    /**
     * Web: true se a página recarregou no meio da captura de foto.
     * Consome a flag. Android/iOS: sempre false.
     */
    fun consumePhotoPickerInterrupted(): Boolean

    /**
     * Web: true se o último capturePhoto/pickImage retornou null porque o usuário
     * cancelou o seletor (não falha de processamento). Consome a flag.
     * Android/iOS: sempre false.
     */
    fun consumeLastPhotoPickCancelled(): Boolean

    /**
     * Web: true se a captura foi bloqueada/indisponível em navegador in-app
     * (WhatsApp, Instagram, Facebook, etc.). Consome a flag.
     * Android/iOS: sempre false.
     */
    fun consumeLastPhotoBlockedInAppBrowser(): Boolean

    /** Fecha o teclado virtual (blur DOM na web / IME no nativo). */
    fun dismissSoftKeyboard()
}

fun formatGpsCoordenadas(lat: Double, lng: Double): String {
    fun fmt(v: Double): String {
        val abs = kotlin.math.abs(v)
        val s = abs.toString()
        val dot = s.indexOf('.')
        return if (dot < 0) s else s.take(dot + 7).trimEnd('0').trimEnd('.')
    }
    val latTxt = "${fmt(lat)}${if (lat < 0) "S" else "N"}"
    val lngTxt = "${fmt(lng)}${if (lng < 0) "W" else "E"}"
    return "$latTxt, $lngTxt"
}
